import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { type PersonnelAssignment } from "@/components/features/matter/information/matter-personnel-assignment-diff";
import { type Matter, type MatterTeam, type MatterTeammate } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import {
  getUserAccountStatus,
  type UserAccountStatus,
} from "@/lib/utils/user-account-status";
import { Users } from "lucide-react";

export function getActiveMatterTeamIds(matter: Matter): string[] {
  return [
    ...new Set(
      matter.teamConnections
        .filter((connection) => !connection.dateTimeEnd)
        .map((connection) => connection.teamId)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
}

export function getTeamMemberIds(
  matter: Matter,
  teamId: string,
  roster: MatterTeammate[],
  restored: PersonnelAssignment[],
) {
  return new Set([
    ...restored.map((member) => member.userId),
    ...roster
      .filter((member) => member.teamId === teamId)
      .map((member) => member.userId),
    ...matter.teamConnections
      .filter(
        (connection) => connection.teamId === teamId && !connection.dateTimeEnd,
      )
      .flatMap((connection) => connection.teammates ?? [])
      .map((member) => member.userId),
  ]);
}

export function getActivePersonnelRoster(
  matter: Matter,
  teamIds: string[],
  newlySelectedTeamIds: string[],
  reselectedTeamIds: string[],
  roster: MatterTeammate[],
) {
  const addedTeamIds = new Set([...newlySelectedTeamIds, ...reselectedTeamIds]);
  return [
    ...new Map(
      [
        ...roster.filter(
          (member) =>
            teamIds.includes(member.teamId) && addedTeamIds.has(member.teamId),
        ),
        ...matter.teamConnections
          .filter(
            (connection) =>
              !connection.dateTimeEnd &&
              connection.teamId &&
              teamIds.includes(connection.teamId) &&
              reselectedTeamIds.includes(connection.teamId),
          )
          .flatMap((connection) => connection.teammates ?? []),
      ].map((member) => [
        `${member.teamId}:${member.userId}:${member.roleId}`,
        member,
      ]),
    ).values(),
  ];
}

export function getTeamNamesByUserId(
  matter: Matter,
  selectedTeamIds: string[],
  newTeamMembers: { teamId: string; userIds: string[] }[],
  teams: MatterTeam[],
) {
  const namesByUser = new Map<string, Set<string>>();
  const addTeam = (userId: string, teamName: string) => {
    const names = namesByUser.get(userId) ?? new Set<string>();
    names.add(teamName);
    namesByUser.set(userId, names);
  };

  for (const connection of matter.teamConnections) {
    if (
      connection.dateTimeEnd ||
      !connection.teamId ||
      !selectedTeamIds.includes(connection.teamId)
    )
      continue;
    for (const teammate of connection.teammates ?? [])
      addTeam(teammate.userId, connection.label);
    for (const assignee of matter.assignees) {
      if (!assignee.dateTimeEnd && assignee.teamConnectionId === connection.id)
        addTeam(assignee.user.id, connection.label);
    }
  }
  for (const { teamId, userIds } of newTeamMembers) {
    const name = teams.find((team) => team.id === teamId)?.label;
    if (!name) continue;
    for (const userId of userIds) addTeam(userId, name);
  }
  return namesByUser;
}

export type PersonnelTeamMember = {
  userId: string;
  name: string;
  role: string;
  accountStatus?: UserAccountStatus;
};

export function getPersonnelUserStatus(
  matter: Matter,
  roster: MatterTeammate[],
  disabledUserIds: Set<string>,
  userId: string,
): UserAccountStatus | undefined {
  const statuses = [
    ...matter.assignees
      .filter((assignee) => assignee.user.id === userId)
      .map((assignee) =>
        getUserAccountStatus(assignee.user.status, assignee.user.enabled),
      ),
    ...matter.teamConnections.flatMap((connection) =>
      (connection.teammates ?? [])
        .filter((teammate) => teammate.userId === userId)
        .map((teammate) => getUserAccountStatus(teammate.status)),
    ),
    ...roster
      .filter((teammate) => teammate.userId === userId)
      .map((teammate) => getUserAccountStatus(teammate.status)),
  ];
  if (statuses.includes("Deleted")) return "Deleted";
  if (statuses.includes("Disabled") || disabledUserIds.has(userId))
    return "Disabled";
  return undefined;
}

export function getTeamMembersByTeamId(
  matter: Matter,
  teamIds: string[],
  roster: MatterTeammate[],
  assignments: PersonnelAssignment[],
  roles: { id: string; label: string }[],
  disabledUserIds: Set<string>,
) {
  const roleById = new Map(roles.map((role) => [role.id, role.label]));
  const rolesByUserId = new Map<string, string[]>();
  for (const assignment of assignments)
    rolesByUserId.set(assignment.userId, [
      ...new Set([
        ...(rolesByUserId.get(assignment.userId) ?? []),
        assignment.roleId,
      ]),
    ]);
  return new Map(
    teamIds.map((teamId) => {
      const connection = matter.teamConnections.find(
        (item) => item.teamId === teamId && !item.dateTimeEnd,
      );
      const members = new Map<string, PersonnelTeamMember>();
      for (const teammate of connection?.teammates ??
        roster.filter((item) => item.teamId === teamId)) {
        members.set(teammate.userId, {
          userId: teammate.userId,
          name: formatUsersName(teammate) ?? "Unknown user",
          role: teammate.roleLabel,
          accountStatus: getUserAccountStatus(
            teammate.status,
            disabledUserIds.has(teammate.userId) ? false : undefined,
          ),
        });
      }
      for (const assignee of matter.assignees) {
        if (
          assignee.dateTimeEnd ||
          assignee.teamConnectionId !== connection?.id
        )
          continue;
        members.set(assignee.user.id, {
          userId: assignee.user.id,
          name: formatUsersName(assignee.user) ?? "Unknown user",
          role: assignee.role.label,
          accountStatus:
            getUserAccountStatus(
              assignee.user.status,
              disabledUserIds.has(assignee.user.id)
                ? false
                : assignee.user.enabled,
            ) ?? members.get(assignee.user.id)?.accountStatus,
        });
      }
      return [
        teamId,
        [...members.values()]
          .map((member) => ({
            ...member,
            role:
              rolesByUserId
                .get(member.userId)
                ?.map((id) => roleById.get(id) ?? id)
                .join(", ") ?? member.role,
          }))
          .sort((left, right) => left.name.localeCompare(right.name)),
      ] as const;
    }),
  );
}

export function PersonnelTeamMembership({ names }: { names?: Set<string> }) {
  if (!names?.size) return null;
  const label = `${names.size === 1 ? "Team" : "Teams"}: ${[...names].join(", ")}`;
  return (
    <TooltipWrapper message={label}>
      <span
        className="text-muted-foreground inline-flex max-w-full items-center gap-1 text-xs"
        tabIndex={0}
        aria-label={label}
      >
        <Users className="size-3.5 shrink-0" />
        <span className="truncate">{[...names].join(", ")}</span>
      </span>
    </TooltipWrapper>
  );
}
